import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Observable, delay, map, of, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { getBackOfficeRuntimeEnv } from '../config/backoffice-runtime-env';
import {
  AdminRole,
  AuthSession,
  AuthUser,
  InvitationAcceptancePayload,
  LoginPayload,
  LoginResponse,
  TwoFactorSetupPayload,
  TwoFactorSetupResponse,
  VerifyTwoFactorPayload,
} from '../../models/auth.model';
import { I18nService } from './i18n.service';

const STORAGE_KEY = 'ccapp.bo.session';

interface LogoutOptions {
  returnUrl?: string;
  message?: {
    summary: string;
    detail: string;
  };
}

interface MockAdminProfile {
  id: string;
  partnerId?: string;
  fullName: string;
  email: string;
  password: string;
  country: string;
  roles: AdminRole[];
  temporaryToken: string;
  twoFactorCode: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private i18n = inject(I18nService);

  private readonly mockProfiles: MockAdminProfile[] = [
    {
      id: 'adm_001',
      fullName: 'Awa Mensah',
      email: 'superadmin@ccapp.io',
      password: '',
      country: '',
      roles: ['ROLE_SUPER_ADMIN'],
      temporaryToken: 'temp-token-super-admin',
      twoFactorCode: '',
    },
    {
      id: 'adm_002',
      fullName: 'Koffi Yao',
      email: 'countryadmin@ccapp.io',
      password: '',
      country: 'CI',
      roles: ['ROLE_COUNTRY_ADMIN'],
      temporaryToken: 'temp-token-country-admin',
      twoFactorCode: '',
    },
    {
      id: 'adm_003',
      fullName: 'Mariam Bello',
      email: 'imfadmin@ccapp.io',
      password: '',
      country: 'NG',
      roles: ['ROLE_IMF_ADMIN'],
      temporaryToken: 'temp-token-imf-admin',
      twoFactorCode: '',
    },
    {
      id: 'adm_004',
      fullName: 'Samuel Boateng',
      email: 'operations@ccapp.io',
      password: '',
      country: 'GH',
      roles: ['ROLE_OPERATIONS'],
      temporaryToken: 'temp-token-operations',
      twoFactorCode: '',
    },
    {
      id: 'adm_005',
      fullName: 'Fatou Ndiaye',
      email: 'support@ccapp.io',
      password: '',
      country: 'SN',
      roles: ['ROLE_SUPPORT'],
      temporaryToken: 'temp-token-support',
      twoFactorCode: '',
    },
    {
      id: 'adm_006',
      partnerId: 'partner-001',
      fullName: 'Nadia Partner',
      email: 'partner.admin@ccapp.io',
      password: '',
      country: 'CI',
      roles: ['ROLE_PARTNER_ADMIN'],
      temporaryToken: 'temp-token-partner-admin',
      twoFactorCode: '',
    },
  ];

  private sessionState = signal<AuthSession | null>(this.loadStoredSession());
  private temporaryTokenState = signal<string | null>(null);
  private logoutInProgress = false;

  readonly session = computed(() => this.sessionState());
  readonly user = computed(() => this.sessionState()?.user ?? null);
  readonly token = computed(() => this.sessionState()?.token ?? null);
  readonly temporaryToken = computed(() => this.temporaryTokenState());
  readonly isAuthenticated = computed(() => Boolean(this.sessionState()?.token));
  readonly isMockMode = computed(() => environment.useMockApi);

  login(payload: LoginPayload): Observable<LoginResponse> {
    if (environment.useMockApi) {
      const profile = this.mockProfiles.find(
        (item) => item.email.toLowerCase() === payload.email.toLowerCase() && item.password === payload.password,
      );

      if (!profile) {
        return throwError(() => new Error('Invalid mock credentials'));
      }

      return of({
        temporaryToken: profile.temporaryToken,
        requiresTwoFactor: true,
      }).pipe(
        delay(500),
        tap((response) => this.temporaryTokenState.set(response.temporaryToken)),
      );
    }

    return this.http.post<unknown>('/api/admin/auth/login', payload).pipe(
      map((response) => this.normalizeLoginResponse(response)),
      tap((response) => this.temporaryTokenState.set(response.temporaryToken)),
    );
  }

  setupTwoFactor(payload: TwoFactorSetupPayload): Observable<TwoFactorSetupResponse> {
    if (environment.useMockApi) {
      return of({
        secret: '',
        qrCodeUrl: null,
        otpAuthUrl: '',
        manualEntryKey: '',
        message: 'Scan the code with Google Authenticator, then enter the 6-digit OTP.',
      }).pipe(delay(500));
    }

    return this.http.post<unknown>('/api/admin/auth/setup-2fa', {
      challengeToken: payload.temporaryToken,
      code: payload.code,
    }).pipe(
      map((response) => this.normalizeTwoFactorSetupResponse(response)),
    );
  }

  activateTwoFactorSetup(payload: VerifyTwoFactorPayload): Observable<AuthSession> {
    if (environment.useMockApi) {
      const profile = this.mockProfiles.find((item) => item.temporaryToken === payload.temporaryToken);

      if (!profile || profile.twoFactorCode !== payload.code) {
        return throwError(() => new Error('Invalid mock 2FA code'));
      }

      const session = this.createMockSession(profile);
      return of(session).pipe(
        delay(500),
        tap((response) => this.persistSession(response)),
      );
    }

    return this.http.post<unknown>('/api/admin/auth/setup-2fa', {
      challengeToken: payload.temporaryToken,
      code: payload.code,
    }).pipe(
      map((response) => this.normalizeAuthSession(response)),
      tap((response) => this.persistSession(response)),
    );
  }

  verifyTwoFactor(payload: VerifyTwoFactorPayload): Observable<AuthSession> {
    if (environment.useMockApi) {
      const profile = this.mockProfiles.find((item) => item.temporaryToken === payload.temporaryToken);

      if (!profile || profile.twoFactorCode !== payload.code) {
        return throwError(() => new Error('Invalid mock 2FA code'));
      }

      const session = this.createMockSession(profile);
      return of(session).pipe(
        delay(500),
        tap((response) => this.persistSession(response)),
      );
    }

    return this.http.post<unknown>('/api/admin/auth/verify-2fa', {
      challengeToken: payload.temporaryToken,
      temporaryToken: payload.temporaryToken,
      tempToken: payload.temporaryToken,
      code: payload.code,
      otpCode: payload.code,
      twoFactorCode: payload.code,
    }).pipe(
      map((response) => this.normalizeAuthSession(response)),
      tap((response) => this.persistSession(response)),
    );
  }

  acceptInvitation(payload: InvitationAcceptancePayload): Observable<void> {
    if (environment.useMockApi) {
      return of(void 0).pipe(delay(700));
    }

    return this.http.post<unknown>('/api/admin/auth/accept-invitation', payload).pipe(
      map(() => void 0),
    );
  }

  logout(options?: LogoutOptions): void {
    if (!this.hasAuthState() || this.logoutInProgress) {
      return;
    }

    this.logoutInProgress = true;
    this.sessionState.set(null);
    this.temporaryTokenState.set(null);
    localStorage.removeItem(STORAGE_KEY);

    if (options?.message) {
      this.messageService.add({
        severity: 'warn',
        summary: options.message.summary,
        detail: options.message.detail,
      });
    }

    const returnUrl = options?.returnUrl ?? this.router.url;
    const queryParams = returnUrl?.startsWith('/') && !returnUrl.startsWith('//')
      ? { returnUrl }
      : undefined;

    void this.router.navigate(['/auth/login'], { queryParams }).finally(() => {
      this.logoutInProgress = false;
    });
  }

  logoutDueToInactivity(): void {
    this.logout({
      message: {
        summary: this.i18n.t('Session expired'),
        detail: this.i18n.t('Session expired due to inactivity'),
      },
    });
  }

  logoutDueToSessionExpiry(): void {
    this.logout({
      message: {
        summary: this.i18n.t('Session expired'),
        detail: this.i18n.t('Your session has reached the maximum duration. Please sign in again.'),
      },
    });
  }

  handleAuthorizationFailure(): void {
    this.logout({
      message: {
        summary: this.i18n.t('Session expired'),
        detail: this.i18n.t('Your session has expired. Please sign in again.'),
      },
    });
  }

  hasAuthState(): boolean {
    return Boolean(this.sessionState()?.token || this.temporaryTokenState());
  }

  replaceAccessToken(token: string): void {
    const currentSession = this.sessionState();
    if (!currentSession || !token) {
      return;
    }

    const refreshedSession: AuthSession = {
      ...currentSession,
      token,
    };
    this.sessionState.set(refreshedSession);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(refreshedSession));
  }

  async refreshAccessToken(backendBaseUrl?: string): Promise<boolean> {
    const token = this.token();
    const resolvedBaseUrl = (backendBaseUrl ?? getBackOfficeRuntimeEnv().BACKEND_BASE_URL)?.replace(/\/+$/, '');
    if (!token || !resolvedBaseUrl) {
      return false;
    }

    try {
      const response = await fetch(`${resolvedBaseUrl}/api/admin/session/keep-alive`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        return false;
      }

      const body = await response.json() as unknown;
      const payload = this.unwrapPayload(body);
      const refreshedToken = this.pickString(payload, ['accessToken', 'token', 'jwt']);
      if (!refreshedToken) {
        return false;
      }

      this.replaceAccessToken(refreshedToken);
      return true;
    } catch {
      return false;
    }
  }

  hasRole(expectedRoles: AdminRole[]): boolean {
    const currentRoles = this.user()?.roles ?? [];
    return expectedRoles.length === 0 || expectedRoles.some((role) => currentRoles.includes(role));
  }

  getMockProfiles(): Array<Pick<MockAdminProfile, 'fullName' | 'email' | 'password' | 'country' | 'roles' | 'twoFactorCode'>> {
    return this.mockProfiles.map(({ fullName, email, password, country, roles, twoFactorCode }) => ({
      fullName,
      email,
      password,
      country,
      roles,
      twoFactorCode,
    }));
  }

  private persistSession(session: AuthSession): void {
    const sessionWithTime: AuthSession = { ...session, loginAt: Date.now() };
    this.sessionState.set(sessionWithTime);
    this.temporaryTokenState.set(null);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionWithTime));
  }

  private loadStoredSession(): AuthSession | null {
    const rawValue = localStorage.getItem(STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    try {
      return JSON.parse(rawValue) as AuthSession;
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
  }

  private createMockSession(profile: MockAdminProfile): AuthSession {
    const mockUser: AuthUser = {
      id: profile.id,
      partnerId: profile.partnerId ?? null,
      fullName: profile.fullName,
      email: profile.email,
      country: profile.country,
      roles: profile.roles,
    };

    return {
      token: `mock-jwt-token-${profile.id}`,
      user: mockUser,
    };
  }

  private normalizeLoginResponse(response: unknown): LoginResponse {
    const candidate = this.unwrapPayload(response);
    const temporaryToken = this.pickString(candidate, ['temporaryToken', 'tempToken', 'challengeToken', 'token', 'accessToken', 'jwt']);
    const requiresTwoFactor = this.pickBoolean(candidate, ['requiresTwoFactor', 'twoFactorRequired', 'requireTwoFactor']) ?? true;
    const requiresTwoFactorSetup = this.pickBoolean(candidate, ['requiresTwoFactorSetup', 'twoFactorSetupRequired']) ?? undefined;

    if (!temporaryToken) {
      throw new Error('Temporary token missing from login response');
    }

    return {
      temporaryToken,
      requiresTwoFactor,
      requiresTwoFactorSetup,
    };
  }

  private normalizeTwoFactorSetupResponse(response: unknown): TwoFactorSetupResponse {
    const candidate = this.unwrapPayload(response);
    const secret = this.pickString(candidate, ['secret', 'secretKey', 'twoFactorSecret']);
    const otpAuthUrl = this.pickString(candidate, ['otpAuthUri', 'otpAuthUrl', 'otpauthUrl', 'otpauth_uri']);
    const qrCodeUrl = this.pickString(candidate, ['qrCodeUrl', 'qrCode', 'qrImageUrl', 'barcode'])
      ?? this.buildQrCodeUrlFromOtpAuth(otpAuthUrl);
    const manualEntryKey = this.pickString(candidate, ['manualEntryKey', 'manualCode', 'secret', 'secretKey', 'twoFactorSecret']);
    const message = this.pickString(candidate, ['message', 'instructions', 'detail']);

    return {
      secret,
      qrCodeUrl,
      otpAuthUrl,
      manualEntryKey,
      message,
    };
  }

  private normalizeAuthSession(response: unknown): AuthSession {
    const candidate = this.unwrapPayload(response);
    const token = this.pickString(candidate, ['token', 'accessToken', 'jwt', 'idToken']);

    if (!token) {
      throw new Error('JWT token missing from 2FA response');
    }

    const nestedUser = this.pickObject(candidate, ['user', 'admin', 'profile']);
    const decodedJwt = this.decodeJwtPayload(token);
    const mergedUser = {
      ...(decodedJwt ?? {}),
      ...(nestedUser ?? {}),
    } as Record<string, unknown>;

    const roles = this.normalizeRoles(mergedUser);

    return {
      token,
      user: {
        id: this.pickString(mergedUser, ['id', 'userId', 'sub']) ?? 'admin-session',
        partnerId: this.pickString(mergedUser, ['partnerId', 'financialPartnerId']),
        fullName: this.pickString(mergedUser, ['fullName', 'name']) ?? this.pickString(mergedUser, ['email', 'preferred_username']) ?? 'Administrator',
        email: this.pickString(mergedUser, ['email', 'preferred_username']) ?? 'admin@unknown.local',
        country: this.pickString(mergedUser, ['country', 'countryCode', 'countryId']) ?? 'N/A',
        roles: roles as AdminRole[],
      },
    };
  }

  private unwrapPayload(value: unknown): Record<string, unknown> {
    if (this.isRecord(value)) {
      if (this.isRecord(value['data'])) {
        return value['data'] as Record<string, unknown>;
      }

      return value;
    }

    return {};
  }

  private decodeJwtPayload(token: string): Record<string, unknown> | null {
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }

    try {
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
      const json = atob(padded);
      const payload = JSON.parse(json) as unknown;
      return this.isRecord(payload) ? payload : null;
    } catch {
      return null;
    }
  }

  private pickString(source: Record<string, unknown>, keys: string[]): string | null {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value;
      }
    }

    return null;
  }

  private buildQrCodeUrlFromOtpAuth(otpAuthUrl: string | null): string | null {
    if (!otpAuthUrl) {
      return null;
    }

    return `https://quickchart.io/qr?size=240&text=${encodeURIComponent(otpAuthUrl)}`;
  }

  private pickBoolean(source: Record<string, unknown>, keys: string[]): boolean | null {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'boolean') {
        return value;
      }
    }

    return null;
  }

  private pickObject(source: Record<string, unknown>, keys: string[]): Record<string, unknown> | null {
    for (const key of keys) {
      const value = source[key];
      if (this.isRecord(value)) {
        return value;
      }
    }

    return null;
  }

  private pickStringArray(source: Record<string, unknown>, keys: string[]): string[] | null {
    for (const key of keys) {
      const value = source[key];
      if (Array.isArray(value)) {
        const strings = value.filter((item): item is string => typeof item === 'string');
        if (strings.length > 0) {
          return strings;
        }
      }
    }

    return null;
  }

  private normalizeRoles(source: Record<string, unknown>): AdminRole[] {
    const rawRoles = this.pickStringArray(source, ['roles', 'authorities']);
    const singularRole = this.pickString(source, ['role', 'authority']);
    const collectedRoles = rawRoles ?? (singularRole ? [singularRole] : []);

    if (collectedRoles.length === 0) {
      return ['ROLE_OPERATIONS'];
    }

    const normalized = collectedRoles.flatMap((role) => {
      switch (role.trim().toUpperCase()) {
        case 'ROLE_SUPER_ADMIN':
        case 'SUPER_ADMIN':
          return ['ROLE_SUPER_ADMIN'];
        case 'ADMIN':
          return ['ROLE_SUPER_ADMIN', 'ADMIN'];
        case 'USER':
          return ['USER'];
        case 'ROLE_COUNTRY_ADMIN':
        case 'COUNTRY_ADMIN':
          return ['ROLE_COUNTRY_ADMIN'];
        case 'ROLE_IMF_ADMIN':
        case 'IMF_ADMIN':
          return ['ROLE_IMF_ADMIN'];
        case 'ROLE_OPERATIONS':
        case 'OPERATIONS':
          return ['ROLE_OPERATIONS'];
        case 'ROLE_SUPPORT':
        case 'SUPPORT':
          return ['ROLE_SUPPORT'];
        case 'ROLE_TREASURER':
        case 'TREASURER':
          return ['ROLE_TREASURER'];
        case 'ROLE_AUDITOR':
        case 'AUDITOR':
          return ['ROLE_AUDITOR'];
        case 'ROLE_PARTNER_ADMIN':
        case 'PARTNER_ADMIN':
          return ['ROLE_PARTNER_ADMIN'];
        case 'ROLE_PARTNER_ANALYST':
        case 'PARTNER_ANALYST':
          return ['ROLE_PARTNER_ANALYST'];
        default:
          return [];
      }
    });

    return normalized.length > 0 ? Array.from(new Set(normalized)) as AdminRole[] : ['ROLE_OPERATIONS'];
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }
}
